// src/components/layout/BusinessVerification.tsx
import React, { useState, useEffect, FormEvent } from "react";
import {
    Container,
    Box,
    Heading,
    Text,
    UnorderedList,
    ListItem,
    FormControl,
    FormLabel,
    Input,
    Button,
    Alert,
    AlertIcon,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useBusinessVerification from "../../hooks/useBusinessVerification.ts";

const BusinessVerification: React.FC = () => {
    const { t } = useTranslation();
    const [businessName, setBusinessName] = useState("");
    const {
        verify,
        isLoading,
        isError,
        error,
        isSuccess,
        redirectUrl,
        // You MUST add these 4 to your hook's return (cards, loading, error (for fetchCards), fetchCards)
        cards,
        loading,
        fetchCards,
        error: cardsError,
    } = useBusinessVerification();

    useEffect(() => {
        fetchCards();
    }, [fetchCards]);

    useEffect(() => {
        if (redirectUrl) {
            window.location.href = redirectUrl;
        }
    }, [redirectUrl]);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (!businessName.trim()) return;
        verify(businessName.trim());
    };

    return (
        <Container
            maxW={{ base: "100%", md: "2xl", lg: "2xl" }}
            px={{ base: 2, md: 0 }}
            pt={{ base: "12vh", md: "12vh" }}
            pb={{ base: 10, md: 16 }}
        >

        <Box bg="white" p={{ base: 6, md: 8 }} borderRadius="md" boxShadow="lg">
                <Heading
                    as="h1"
                    size="lg"
                    textAlign="center"
                    mb={{ base: 4, md: 6 }}
                >
                    {t("business_verification")}
                </Heading>

                {/* Verified Business Cards Table */}
                <Box mb={6}>
                    <Heading as="h2" size="md" mb={3}>
                        {t("Your Verified Business Cards")}
                    </Heading>
                    {loading ? (
                        <Spinner size="lg" />
                    ) : cardsError ? (
                        <Alert status="error" borderRadius="md" mb={4}>
                            <AlertIcon /> {cardsError.message}
                        </Alert>
                    ) : !cards || cards.length === 0 ? (
                        <Text color="gray.500" fontSize="sm">
                            {t("No verified business cards found.")}
                        </Text>
                    ) : (
                        <Table size="sm" variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Card ID</Th>
                                    <Th>BUSINESS Name</Th>
                                    <Th>Card Mask</Th>
                                    <Th>Created At</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {cards.map((card) => (
                                    <Tr key={card.cardId}>
                                        <Td>{card.cardId}</Td>
                                        <Td>{card.userName}</Td>
                                        <Td>
                                            {card.cardMask ? card.cardMask : <span style={{ color: "#999" }}>—</span>}
                                        </Td>

                                        <Td
                                            sx={{
                                                maxWidth: { base: "72px", md: "none" },
                                                wordBreak: "break-all",
                                                fontSize: { base: "xs", md: "sm" },
                                                whiteSpace: { base: "normal", md: "nowrap" },
                                            }}
                                        >
                                            {(() => {
                                                const date = new Date(card.createdAt);
                                                // Short format for mobile, full for desktop
                                                return window.innerWidth < 768
                                                    ? date.toLocaleDateString("en-GB") // e.g. "17/05/2025"
                                                    : date.toLocaleString("en-GB", {
                                                        year: "numeric",
                                                        month: "2-digit",
                                                        day: "2-digit",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    });
                                            })()}
                                        </Td>

                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>

                    )}
                </Box>

                {/* Description */}
                <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="gray.700"
                    mb={4}
                    lineHeight="tall"
                >
                    Bu bölmə biznes hesablarının təsdiqi üçündür. Aşağıdakı form vasitəsilə biznes
                    adınızı daxil etdikdən sonra, səhifə avtomatik kart qeydiyyatına yönləndiriləcək
                    (PashaBank). 24 saat ərzində Epoint.az sizin kartınızın biznes kartı olub-olmadığını
                    yoxlayıb təsdiq edəcək. Kart aktivləşdikdən sonra keçirilən tədbirə dair ödəniş
                    məsələləri üçün bizə müraciət edə biləcəksiniz.
                </Text>

                {/* Notes */}
                <Box
                    bg="gray.50"
                    p={4}
                    borderLeft="4px solid"
                    borderColor="teal.400"
                    borderRadius="md"
                    mb={6}
                >
                    <Text fontWeight="semibold" mb={2}>
                        Qeyd:
                    </Text>
                    <UnorderedList spacing={2} ml={4} fontSize={{ base: "sm", md: "md" }}>
                        <ListItem>Kart aktivləşdirmə müddəti maksimum 24 saatdır.</ListItem>
                        <ListItem>
                            Formu tam və düzgün doldurmaq təsdiq prosesini sürətləndirir.
                        </ListItem>
                        <ListItem>
                            Suallarınız varsa, yazın:{" "}
                            <Text as="span" color="teal.600" fontWeight="medium">
                                info@eventeam.az
                            </Text>
                        </ListItem>
                    </UnorderedList>
                </Box>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <FormControl id="business-name" isRequired mb={4}>
                        <FormLabel>{t("Business Name")}</FormLabel>
                        <Input
                            placeholder={t("Enter your business name")}
                            value={businessName}
                            onChange={(e) => setBusinessName(e.target.value)}
                            size="lg"
                        />
                    </FormControl>

                    {isError && (
                        <Alert status="error" mb={4} borderRadius="md">
                            <AlertIcon />
                            {error?.message || t("An error occurred. Please try again.")}
                        </Alert>
                    )}

                    <Button
                        type="submit"
                        colorScheme="teal"
                        w="100%"
                        size="lg"
                        isLoading={isLoading}
                        loadingText={t("Submitting")}
                        mb={isSuccess ? 0 : 6}
                    >
                        {t("Submit")}
                    </Button>
                </form>

                {isSuccess && (
                    <Alert status="success" mt={6} borderRadius="md">
                        <AlertIcon />
                        {t("Redirecting you to complete card registration...")}
                    </Alert>
                )}
            </Box>
        </Container>
    );
};

export default BusinessVerification;
